import React, { useState, useEffect } from 'react';
import { useLMS } from '../../context/LMSContext';
import { db } from '../../lib/supabase';

const StudentNotesPanel = ({ student, isOpen, onClose }) => {
  const { currentUser } = useLMS();
  const [notes, setNotes] = useState([]);
  const [percentiles, setPercentiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('notes');
  const [showAddNote, setShowAddNote] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  
  const [newNote, setNewNote] = useState({
    noteType: 'meeting',
    title: '',
    content: '',
    meetingDate: new Date().toISOString().split('T')[0]
  });

  const [newPercentile, setNewPercentile] = useState({
    weekNumber: 1,
    percentile: '',
    questionsCompleted: '',
    source: 'uworld',
    notes: ''
  });

  // Load data when panel opens
  useEffect(() => {
    if (isOpen && student?.id) {
      loadStudentData();
    }
  }, [isOpen, student?.id]);

  const loadStudentData = async () => {
    setLoading(true);
    try {
      const [notesRes, percentilesRes] = await Promise.all([
        db.getStudentNotes(student.id),
        db.getStudentPercentiles(student.id)
      ]);

      if (notesRes.data) setNotes(notesRes.data);
      if (percentilesRes.data) setPercentiles(percentilesRes.data);
    } catch (error) {
      console.error('Error loading student data:', error);
    }
    setLoading(false);
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    try {
      const { data, error } = await db.createStudentNote({
        studentId: student.id,
        adminId: currentUser.id,
        noteType: newNote.noteType,
        title: newNote.title,
        content: newNote.content,
        meetingDate: newNote.noteType === 'meeting' ? newNote.meetingDate : null
      });

      if (error) throw error;

      setNotes(prev => [data, ...prev]);
      setShowAddNote(false);
      setNewNote({
        noteType: 'meeting',
        title: '',
        content: '',
        meetingDate: new Date().toISOString().split('T')[0]
      });
    } catch (error) {
      console.error('Error adding note:', error);
      alert('Failed to add note: ' + error.message);
    }
  };

  const handleUpdateNote = async (e) => {
    e.preventDefault();
    try {
      const { data, error } = await db.updateStudentNote(editingNote.id, {
        noteType: editingNote.note_type,
        title: editingNote.title,
        content: editingNote.content,
        meetingDate: editingNote.meeting_date
      });

      if (error) throw error;

      setNotes(prev => prev.map(n => n.id === editingNote.id ? data : n));
      setEditingNote(null);
    } catch (error) {
      console.error('Error updating note:', error);
      alert('Failed to update note: ' + error.message);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;
    
    try {
      const { error } = await db.deleteStudentNote(noteId);
      if (error) throw error;
      setNotes(prev => prev.filter(n => n.id !== noteId));
    } catch (error) {
      console.error('Error deleting note:', error);
      alert('Failed to delete note: ' + error.message);
    }
  };

  const handleSavePercentile = async (e) => {
    e.preventDefault();
    try {
      const { data, error } = await db.upsertStudentPercentile({
        studentId: student.id,
        weekNumber: parseInt(newPercentile.weekNumber),
        percentile: parseInt(newPercentile.percentile),
        questionsCompleted: parseInt(newPercentile.questionsCompleted) || 0,
        source: newPercentile.source,
        notes: newPercentile.notes,
        recordedBy: currentUser.id
      });

      if (error) throw error;

      // Update or add the percentile in state
      setPercentiles(prev => {
        const existing = prev.findIndex(p => 
          p.week_number === data.week_number && p.source === data.source
        );
        if (existing >= 0) {
          const updated = [...prev];
          updated[existing] = data;
          return updated;
        }
        return [...prev, data].sort((a, b) => a.week_number - b.week_number);
      });

      setNewPercentile({
        weekNumber: Math.min(6, parseInt(newPercentile.weekNumber) + 1),
        percentile: '',
        questionsCompleted: '',
        source: 'uworld',
        notes: ''
      });
    } catch (error) {
      console.error('Error saving percentile:', error);
      alert('Failed to save percentile: ' + error.message);
    }
  };

  const getNoteTypeIcon = (type) => {
    const icons = {
      meeting: '📅',
      general: '📝',
      goal: '🎯',
      concern: '⚠️'
    };
    return icons[type] || '📝';
  };

  const getNoteTypeLabel = (type) => {
    const labels = {
      meeting: 'Meeting Note',
      general: 'General Note',
      goal: 'Goal',
      concern: 'Concern'
    };
    return labels[type] || type;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const getPercentileColor = (percentile) => {
    if (percentile >= 70) return 'var(--success)';
    if (percentile >= 50) return 'var(--blue-primary)';
    if (percentile >= 30) return '#FFA726';
    return 'var(--error)';
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal student-notes-panel" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="student-header-info">
            <div className="student-avatar">
              {student?.name?.charAt(0)?.toUpperCase() || '?'}
            </div>
            <div>
              <h3>{student?.name}</h3>
              <p className="student-email">{student?.email}</p>
            </div>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {/* Tabs */}
        <div className="panel-tabs">
          <button 
            className={`tab-btn ${activeTab === 'notes' ? 'active' : ''}`}
            onClick={() => setActiveTab('notes')}
          >
            📝 Notes ({notes.length})
          </button>
          <button 
            className={`tab-btn ${activeTab === 'percentiles' ? 'active' : ''}`}
            onClick={() => setActiveTab('percentiles')}
          >
            📊 Percentiles
          </button>
        </div>

        <div className="panel-content">
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading...</p>
            </div>
          ) : activeTab === 'notes' ? (
            /* Notes Tab */
            <div className="notes-tab">
              <div className="tab-actions">
                <button 
                  className="btn-primary"
                  onClick={() => setShowAddNote(true)}
                >
                  + Add Note
                </button>
              </div>

              {/* Add Note Form */}
              {showAddNote && (
                <form className="note-form" onSubmit={handleAddNote}>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Note Type</label>
                      <select
                        value={newNote.noteType}
                        onChange={(e) => setNewNote({...newNote, noteType: e.target.value})}
                      >
                        <option value="meeting">📅 Meeting Note</option>
                        <option value="general">📝 General Note</option>
                        <option value="goal">🎯 Goal</option>
                        <option value="concern">⚠️ Concern</option>
                      </select>
                    </div>
                    {newNote.noteType === 'meeting' && (
                      <div className="form-group">
                        <label>Meeting Date</label>
                        <input
                          type="date"
                          value={newNote.meetingDate}
                          onChange={(e) => setNewNote({...newNote, meetingDate: e.target.value})}
                        />
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Title (optional)</label>
                    <input
                      type="text"
                      placeholder="e.g., Week 2 Check-in"
                      value={newNote.title}
                      onChange={(e) => setNewNote({...newNote, title: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Content</label>
                    <textarea
                      rows={4}
                      placeholder="Enter your notes here..."
                      value={newNote.content}
                      onChange={(e) => setNewNote({...newNote, content: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-actions">
                    <button type="button" className="btn-secondary" onClick={() => setShowAddNote(false)}>
                      Cancel
                    </button>
                    <button type="submit" className="btn-primary">
                      Save Note
                    </button>
                  </div>
                </form>
              )}

              {/* Notes List */}
              <div className="notes-list">
                {notes.length === 0 ? (
                  <div className="empty-state small">
                    <span>📝</span>
                    <p>No notes yet for this student</p>
                  </div>
                ) : (
                  notes.map(note => (
                    <div key={note.id} className={`note-card ${note.note_type}`}>
                      {editingNote?.id === note.id ? (
                        <form onSubmit={handleUpdateNote} className="note-edit-form">
                          <div className="form-group">
                            <textarea
                              rows={3}
                              value={editingNote.content}
                              onChange={(e) => setEditingNote({...editingNote, content: e.target.value})}
                              required
                            />
                          </div>
                          <div className="form-actions">
                            <button type="button" className="btn-secondary small" onClick={() => setEditingNote(null)}>
                              Cancel
                            </button>
                            <button type="submit" className="btn-primary small">
                              Save
                            </button>
                          </div>
                        </form>
                      ) : (
                        <>
                          <div className="note-header">
                            <div className="note-type-badge">
                              {getNoteTypeIcon(note.note_type)} {getNoteTypeLabel(note.note_type)}
                            </div>
                            <div className="note-actions">
                              <button 
                                className="btn-icon small"
                                onClick={() => setEditingNote(note)}
                                title="Edit"
                              >
                                ✏️
                              </button>
                              <button 
                                className="btn-icon small"
                                onClick={() => handleDeleteNote(note.id)}
                                title="Delete"
                              >
                                🗑️
                              </button>
                            </div>
                          </div>
                          {note.title && <h4 className="note-title">{note.title}</h4>}
                          <p className="note-content">{note.content}</p>
                          <div className="note-meta">
                            {note.meeting_date && (
                              <span>📅 {formatDate(note.meeting_date)}</span>
                            )}
                            <span>Added {formatDate(note.created_at)}</span>
                          </div>
                        </>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          ) : (
            /* Percentiles Tab */
            <div className="percentiles-tab">
              {/* Quick Entry Form */}
              <form className="percentile-form" onSubmit={handleSavePercentile}>
                <h4>📊 Record Percentile</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>Week</label>
                    <select
                      value={newPercentile.weekNumber}
                      onChange={(e) => setNewPercentile({...newPercentile, weekNumber: e.target.value})}
                    >
                      {[1, 2, 3, 4, 5, 6].map(w => (
                        <option key={w} value={w}>Week {w}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Source</label>
                    <select
                      value={newPercentile.source}
                      onChange={(e) => setNewPercentile({...newPercentile, source: e.target.value})}
                    >
                      <option value="uworld">UWorld</option>
                      <option value="nclex_high_yield">NCLEX High Yield</option>
                      <option value="kaplan">Kaplan</option>
                      <option value="hurst">Hurst</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Percentile</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      placeholder="e.g., 65"
                      value={newPercentile.percentile}
                      onChange={(e) => setNewPercentile({...newPercentile, percentile: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Questions</label>
                    <input
                      type="number"
                      min="0"
                      placeholder="# done"
                      value={newPercentile.questionsCompleted}
                      onChange={(e) => setNewPercentile({...newPercentile, questionsCompleted: e.target.value})}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Notes (optional)</label>
                  <input
                    type="text"
                    placeholder="Any observations..."
                    value={newPercentile.notes}
                    onChange={(e) => setNewPercentile({...newPercentile, notes: e.target.value})}
                  />
                </div>
                <button type="submit" className="btn-primary">
                  Save Percentile
                </button>
              </form>

              {/* Percentile History */}
              <div className="percentile-history">
                <h4>📈 Progress History</h4>
                {percentiles.length === 0 ? (
                  <div className="empty-state small">
                    <span>📊</span>
                    <p>No percentiles recorded yet</p>
                  </div>
                ) : (
                  <div className="percentile-grid">
                    <div className="percentile-header">
                      <span>Week</span>
                      <span>Source</span>
                      <span>Percentile</span>
                      <span>Questions</span>
                      <span>Notes</span>
                    </div>
                    {percentiles.map(p => (
                      <div key={p.id} className="percentile-row">
                        <span className="week-num">Week {p.week_number}</span>
                        <span className="source-badge">{p.source}</span>
                        <span 
                          className="percentile-value"
                          style={{ color: getPercentileColor(p.percentile) }}
                        >
                          {p.percentile}%
                        </span>
                        <span className="questions">{p.questions_completed || '-'}</span>
                        <span className="notes">{p.notes || '-'}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Visual Chart */}
                {percentiles.length > 0 && (
                  <div className="percentile-chart">
                    <h4>📉 Trend</h4>
                    <div className="chart-container">
                      {[1, 2, 3, 4, 5, 6].map(week => {
                        const weekData = percentiles.find(p => p.week_number === week);
                        const height = weekData ? weekData.percentile : 0;
                        return (
                          <div key={week} className="chart-bar-container">
                            <div 
                              className="chart-bar"
                              style={{ 
                                height: `${height}%`,
                                backgroundColor: weekData ? getPercentileColor(weekData.percentile) : 'var(--gray-200)'
                              }}
                            >
                              {weekData && (
                                <span className="bar-label">{weekData.percentile}%</span>
                              )}
                            </div>
                            <span className="week-label">W{week}</span>
                          </div>
                        );
                      })}
                    </div>
                    <div className="chart-baseline">
                      <span className="baseline-label">Target: 60%</span>
                      <div className="baseline-line" style={{ bottom: '60%' }}></div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentNotesPanel;
